import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  Droplets,
  MapPinned,
  Plug,
  ShieldCheck,
  Snowflake,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { isNativeUi } from '../../utils/nativeUi';

const STORAGE_KEY = 'fixnow:native-onboarding-done';

interface Slide {
  title: string;
  subtitle: string;
  bullets: string[];
  icon: LucideIcon;
  image?: string;
}

const SLIDES: Slide[] = [
  {
    title: '智慧即時\n到府維修',
    subtitle: '水電、冷氣、家電大小問題，一鍵找到附近的專業師傅。',
    bullets: ['依您的位置媒合附近工匠', '專業師傅即時派工到府'],
    icon: Wrench,
    image: '/onboarding-hero.jpg',
  },
  {
    title: '師傅在哪裡\n一目了然',
    subtitle: '接案後即時追蹤師傅位置與預計到達時間。',
    bullets: ['地圖即時顯示師傅動態', '一鍵撥號、訊息聯繫'],
    icon: MapPinned,
  },
  {
    title: '透明收費\n安心付款',
    subtitle: '工時驗收後再付尾款，評價制度把關服務品質。',
    bullets: ['綠界金流安全付款', '完工驗收、評價保障'],
    icon: ShieldCheck,
  },
];

const readDone = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const NativeAppOnboarding: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = useState(
    () => isNativeUi() && location.pathname === '/' && !readDone()
  );
  const [index, setIndex] = useState(0);

  if (!open) return null;

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // 無法寫入時僅關閉本次顯示
    }
    setOpen(false);
  };

  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index];
  const SlideIcon = slide.icon;

  return (
    <div className="fixnow-onboarding fixed inset-0 z-[300] flex flex-col bg-gradient-to-b from-[#eef3fb] via-[#f4f6fb] to-white pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]">
      <div className="flex justify-end px-5 pt-4">
        {!isLast && (
          <button
            type="button"
            onClick={finish}
            className="text-sm font-medium text-slate-500 px-3 py-2 min-h-[44px]"
          >
            略過
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          className="flex-1 min-h-0 overflow-y-auto flex flex-col px-7"
        >
          <h1 className="whitespace-pre-line text-center text-[2rem] leading-tight font-extrabold text-[#1d3557] mt-2">
            {slide.title}
          </h1>

          {slide.image ? (
            <img
              src={slide.image}
              alt=""
              className="mx-auto my-5 h-56 w-56 shrink-0 rounded-[2rem] object-cover [@media(min-height:740px)]:my-6 [@media(min-height:740px)]:h-72 [@media(min-height:740px)]:w-72"
            />
          ) : (
            <div className="relative mx-auto my-6 h-48 w-48 shrink-0 [@media(min-height:740px)]:my-8 [@media(min-height:740px)]:h-56 [@media(min-height:740px)]:w-56">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#dbe7f7] to-[#c3d6f0]" />
              <div className="absolute inset-6 rounded-full bg-white/70 shadow-inner" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-28 w-28 items-center justify-center rounded-[2rem] bg-gradient-to-br from-[#1d3557] to-[#3d5a80] shadow-2xl">
                  <SlideIcon className="h-14 w-14 text-white" strokeWidth={1.75} />
                </div>
              </div>
              <span className="absolute left-2 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-lg">
                <Plug className="h-5 w-5 text-amber-500" />
              </span>
              <span className="absolute right-0 top-12 flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-lg">
                <Snowflake className="h-5 w-5 text-sky-500" />
              </span>
              <span className="absolute bottom-4 left-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-lg">
                <Droplets className="h-5 w-5 text-blue-500" />
              </span>
            </div>
          )}

          <p className="text-center text-base text-slate-600">{slide.subtitle}</p>

          <ul className="mx-auto mt-5 space-y-2.5">
            {slide.bullets.map((bullet) => (
              <li key={bullet} className="flex items-center gap-2.5 text-[15px] text-slate-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#e63946]/10">
                  <Check className="h-3.5 w-3.5 text-[#e63946]" strokeWidth={3} />
                </span>
                {bullet}
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>

      <div className="shrink-0 px-7 pb-6">
        <button
          type="button"
          onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
          className="w-full rounded-2xl bg-[#e63946] py-4 text-lg font-bold text-white shadow-lg shadow-[#e63946]/30 active:bg-[#c1121f] transition-colors"
        >
          {isLast ? '開始使用' : '下一步'}
        </button>
        <div className="mt-5 flex justify-center gap-2" aria-hidden>
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-6 bg-[#1d3557]' : 'w-2 bg-slate-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NativeAppOnboarding;
